import { Component, OnInit } from '@angular/core';
import { PillarLayout, PillarConfig } from '../../../widgets/pillar-layout/pillar-layout';

import { MetaDataService } from '../../../core/services/meta-data.service';

import { SeoTitleStrategy } from '../../../core/seo/seo-title-strategy';

import { ModernCta } from '../../../widgets/modern-cta/modern-cta';

@Component({
  selector: 'app-what-is-cmdb',
  imports: [PillarLayout, ModernCta],
  templateUrl: './what-is-cmdb.html',
  styleUrl: './what-is-cmdb.scss',
})
export class WhatIsCmdb implements OnInit {
  accentColor = '#FF652C';

  config: PillarConfig = {
    tocItems: [
      { id: 'what-is-cmdb', label: 'What is CMDB?' },
      { id: 'cmdb-vs-itar', label: 'CMDB vs IT Asset Register' },
      { id: 'key-uses-cmdb', label: 'Key Uses of a CMDB' },
      { id: 'faq', label: 'Frequently Asked Questions' },
    ],
    definition: {
      id: 'what-is-cmdb',
      eyebrow: 'Definition',
      title: 'What is a CMDB?',
      definitionHtml: `A Configuration Management Database (CMDB) is a centralised repository that stores information about all IT components - called Configuration Items (CIs) - and the relationships between them. A CMDB maps how infrastructure, applications, services, and people are connected, enabling IT teams to understand the impact of any change or failure. `,
      definitionCite: '- Configuration Management Database - Complete Guide',
      bodyHtml: [
        `Unlike a simple asset inventory, a CMDB captures not just what assets exist but how they relate to each other - which server runs which application, which application supports which business service, and which users depend on which infrastructure. This relationship mapping is what makes a CMDB indispensable for change management, incident resolution, and root cause analysis.`,
      ],
    },
    compare: {
      id: 'cmdb-vs-itar',
      eyebrow: 'Comparisons',
      title: 'CMDB vs IT Asset Register - Key Differences',
      intro:
        'A CMDB and an IT Asset Register (ITAR) are complementary tools that serve different purposes. While both track IT assets, they differ in scope, focus, and the type of information they store. Understanding these differences helps organisations leverage both systems effectively.',
      headers: ['Dimension', 'Asset Register', 'CMDB'],
      rows: [
        {
          term: 'Purpose',
          termFull: '',
          tagClass: 'blue',
          manages: 'Financial and lifecycle tracking',
          focus: 'Relationship and dependency mapping',
        },
        {
          term: 'Scope',
          termFull: '',
          tagClass: 'orange',
          manages: 'Cost, ownership, warranty, lifecycle',
          focus: 'Configuration, dependencies, relationships',
        },
        {
          term: 'Primary user',
          termFull: '',
          tagClass: 'teal',
          manages: 'IT asset managers, finance, procurement',
          focus: 'IT ops, change management, service desk',
        },
        {
          term: 'Relationship',
          termFull: '',
          tagClass: 'blue',
          manages: 'Minimal',
          focus: 'Full configuration change history',
        },
      ],
    },
    benefits: {
      id: 'key-uses-cmdb',
      eyebrow: 'Key Uses of CMDB',
      title: 'Key Uses of a CMDB',
      intro:
        'A CMDB is a foundational tool for IT service management (ITSM) and IT operations. It enables organisations to manage change, resolve incidents faster, and maintain compliance by providing a clear view of the IT environment and its dependencies.',
      items: [
        {
          num: '01',
          title: 'Change impact analysis',
          description:
            'Before approving a change, understand which services and users will be affected',
        },
        {
          num: '02',
          title: 'Incident root cause analysis',
          description: 'Trace a service outage back to the failing CI and all dependent components',
        },
        {
          num: '03',
          title: 'Service dependency mapping',
          description:
            'Visualise how applications, servers, and network components support each business service',
        },
        {
          num: '04',
          title: 'Compliance and auditing',
          description:
            'Maintain a verified record of all infrastructure configurations for audit purposes',
        },
        {
          num: '05',
          title: 'Capacity planning',
          description:
            'Understand current CI relationships to model the impact of growth or consolidation',
        },
      ],
    },
    faq: {
      id: 'faq',
      eyebrow: 'FAQ',
      title: 'Frequently Asked Questions',
      items: [
        {
          question: 'What does CI mean in a CMDB?',
          open: true,
          answer:
            'CI stands for Configuration Item - any component that needs to be managed and tracked in the IT environment. CIs include servers, network devices, applications, databases, virtual machines, cloud resources, and even people or business processes when they are part of a service delivery chain.',
        },
        {
          question: 'Is a CMDB the same as an asset management database?',
          open: false,
          answer:
            'No. An asset management database tracks ownership, cost, and lifecycle of IT assets. A CMDB tracks the configuration and relationships between IT components. They serve different purposes and are complementary - asset data feeds into the CMDB, and CMDB relationship data informs asset decisions.',
        },
        {
          question: 'How is a CMDB populated?',
          open: false,
          answer:
            'A CMDB is populated through a combination of automated discovery (scanning the network to detect devices, software, and relationships), manual entry, and integration with monitoring and ITSM tools. Automated discovery is essential for maintaining an accurate, up-to-date CMDB at scale.',
        },
        {
          question: 'What makes a CMDB successful?',
          open: false,
          answer:
            'The most common cause of CMDB failure is poor data quality. A successful CMDB requires automated discovery to keep data current, clearly defined CI types and relationship models, integration with change management to track configuration changes, and executive sponsorship to enforce data governance.',
        },
        {
          question: 'What is a CMDB used for in IT?',
          open: false,
          answer:
            'A CMDB (Configuration Management Database) is used for five key purposes in IT: change impact analysis (understanding which services and users will be affected before approving a change); incident root cause analysis (tracing a service outage back to the failing CI and all dependent components); service dependency mapping (visualising how applications, servers, and network components support each business service); compliance and auditing (maintaining a verified record of all infrastructure configurations); and capacity planning (modelling the impact of growth or consolidation based on current CI relationships).',
        },
        {
          question: 'What is the difference between a CMDB and an IT asset register?',
          open: false,
          answer:
            'The key difference is purpose and data focus. An IT asset register is primarily used for financial and lifecycle tracking - recording cost, ownership, warranty, and procurement data. A CMDB is used for relationship and dependency mapping - recording configuration, dependencies, and change history. The asset register is used by IT asset managers, finance, and procurement teams; the CMDB is used by IT operations, change management, and the service desk. In practice, the asset register feeds hardware data into the CMDB, and both are needed together.',
        },
      ],
    },
  };

  constructor(
    private metaService: MetaDataService,
    private seoStrategy: SeoTitleStrategy,
  ) {
    const ratingSchema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      '@id': 'https://infraon.io/it-asset-management#software',
      name: 'IT Asset Management Software',
      url: 'https://infraon.io/it-asset-management',
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'IT Asset Management Software',
      operatingSystem: 'Web',
      description:
        'Infraon IT Asset Management Software is a cloud-based ITAM platform that gives enterprise IT teams complete visibility into hardware, software, and cloud assets across their full lifecycle - from procurement to secure disposal. Includes built-in CMDB integration for configuration and dependency mapping.',
      featureList: [
        'CMDB Integration',
        'Hardware Asset Tracking',
        'Software Asset Management (SAM)',
        'Cloud Asset Management',
        'IT Asset Lifecycle Management',
        'Asset Discovery and Inventory',
        'License Compliance Management',
        'IT Asset Register',
        'Secure Asset Retirement and Data Wiping',
        'Asset Optimisation and Rightsizing',
        'Warranty and Contract Tracking',
        'Procurement and Vendor Management',
      ],
      publisher: {
        '@type': 'Organization',
        name: 'Infraon',
        url: 'https://infraon.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://infraon.io/assets/img/logos/website/org-logo.jpg',
          width: 200,
          height: 60,
        },
      },
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '155.00',
        highPrice: '524.00',
        priceCurrency: 'USD',
        offerCount: '3',
        offers: [
          {
            '@type': 'Offer',
            name: 'Essential',
            description:
              'For small IT teams that need fast, reliable asset tracking and lifecycle visibility. Priced per month for 1000 assets with 10 users.',
            price: '155.00',
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '155.00',
              priceCurrency: 'USD',
              unitText: 'per month for 1000 assets / 10 users',
            },
            url: 'https://infraon.io/it-asset-management#pricing',
            eligibleCustomerType: 'http://purl.org/goodrelations/v1#Business',
          },
          {
            '@type': 'Offer',
            name: 'Standard',
            description:
              'For scaling IT teams that need smarter workflows, license management, and third-party integrations. Priced per month for 1000 assets with 10 users.',
            price: '347.00',
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '347.00',
              priceCurrency: 'USD',
              unitText: 'per month for 1000 assets / 10 users',
            },
            url: 'https://infraon.io/it-asset-management#pricing',
            eligibleCustomerType: 'http://purl.org/goodrelations/v1#Business',
          },
          {
            '@type': 'Offer',
            name: 'Professional',
            description:
              'For enterprises that need full ITAM coverage including CMDB integration, advanced automation, and AI-assisted asset intelligence. Priced per month for 1000 assets with 10 users.',
            price: '524.00',
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '524.00',
              priceCurrency: 'USD',
              unitText: 'per month for 1000 assets / 10 users',
            },
            url: 'https://infraon.io/it-asset-management#pricing',
            eligibleCustomerType: 'http://purl.org/goodrelations/v1#Business',
          },
        ],
      },
      audience: {
        '@type': 'Audience',
        audienceType: 'IT Teams, IT Managers, IT Analysts, Enterprise IT Teams',
      },
    };

    this.seoStrategy.injectratingSchema('1', JSON.stringify(ratingSchema));

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': 'https://infraon.io/it-asset-management/what-is-cmdb#article',
      headline: 'What is a CMDB? Configuration Management Database Explained',
      description:
        'Learn what a CMDB is, what CMDB stands for, how it works in IT asset management, and how it differs from an asset register - with real-world use cases for IT teams.',
      url: 'https://infraon.io/it-asset-management/what-is-cmdb',
      inLanguage: 'en',
      author: {
        '@type': 'Organization',
        name: 'Infraon',
        url: 'https://infraon.io',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Infraon',
        url: 'https://infraon.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://infraon.io/assets/img/logos/website/org-logo.jpg',
          width: 200,
          height: 60,
        },
      },
      image: {
        '@type': 'ImageObject',
        url: 'https://infraon.io/assets/img/feature-imgs/assets/sass-asset-hero.png',
        width: 1200,
        height: 630,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': 'https://infraon.io/it-asset-management/what-is-cmdb',
      },
      about: [
        {
          '@type': 'Thing',
          name: 'Configuration Management Database',
          alternateName: 'CMDB',
          sameAs: 'https://en.wikipedia.org/wiki/Configuration_management_database',
        },
        {
          '@type': 'Thing',
          name: 'Configuration Item',
          alternateName: 'CI',
          sameAs: 'https://en.wikipedia.org/wiki/Configuration_item',
        },
        {
          '@type': 'Thing',
          name: 'IT Asset Management',
          alternateName: 'ITAM',
          sameAs: 'https://en.wikipedia.org/wiki/IT_asset_management',
        },
      ],
      keywords: [
        'what is a CMDB',
        'what does CMDB stand for',
        'what is CMDB in asset management',
        'Configuration Management Database',
        'CMDB vs asset register',
        'configuration item',
        'CMDB uses',
        'CMDB IT asset management',
        'CMDB change management',
      ],
      articleSection: [
        'What is a CMDB',
        'What Does CMDB Stand For',
        'CMDB in Asset Management',
        'Key Uses of a CMDB',
        'CMDB vs IT Asset Register',
        'CMDB FAQ',
      ],
      isPartOf: {
        '@type': 'WebSite',
        name: 'Infraon',
        url: 'https://infraon.io',
      },
      mentions: [
        {
          name: 'Infraon IT Asset Management Software',
          url: 'https://infraon.io/it-asset-management',
        },
      ],
    };

    this.seoStrategy.injectratingSchema('itam-article-schema', JSON.stringify(articleSchema));
  }

  ngOnInit(): void {
    this.metaService.updateFaqJsonLd(
      this.config.faq.items.map((f) => ({
        question: f.question,
        answer: { paragraphs: [f.answer] },
      })),
    );
  }
}
